import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Car, Fuel, Gauge, Search, Upload, Download, X, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  brand: "", model: "", version: "", trim: "", stock_type: "usado", stock_reference: "",
  price: 0, price_financed: 0, monthly_payment_from: 0, finance_entry: 0, finance_term_months: 0, vat_deductible: false,
  year: new Date().getFullYear(), registration_date: "", mileage: 0, fuel_type: "gasolina", transmission: "manual",
  power_cv: 0, displacement_cc: 0, eco_label: "", body_type: "", color: "", color_interior: "",
  doors: 5, seats: 5, traction: "", consumption_mixed: "", co2_emissions: "", boot_capacity: "",
  warranty_months: 12, warranty_extendable: false, certified_mileage: false, structural_damage_free: false,
  maintenance_history_available: false, inspection_points: 0, delivery_available: true, trade_in_available: true,
  status: "disponible", commercial_status: "active", available_from: "",
  short_description: "", long_description: "",
  featured_equipment_text: "", full_equipment_text: "",
  center_id: "", advisor_id: "",
  featured: false, is_reserved: false, is_sold: false,
  vehicle_type: "usado", description: ""
};

const TABS = [
  { key: "identity", label: "Identificacion" },
  { key: "pricing", label: "Precio" },
  { key: "specs", label: "Especificaciones" },
  { key: "trust", label: "Garantía" },
  { key: "equipment", label: "Equipamiento" },
  { key: "description", label: "Descripcion" },
  { key: "relations", label: "Centro / Vendedor" },
  { key: "images", label: "Imagenes" },
];

export default function VehiclesPage() {
  const { axiosAuth } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("todos");
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({...EMPTY_FORM});
  const [tab, setTab] = useState("identity");
  const [centers, setCenters] = useState([]);
  const [sellers, setSellers] = useState([]);

  const fetchVehicles = useCallback(async () => {
    try {
      const params = {};
      if (typeFilter !== "todos") params.vehicle_type = typeFilter;
      const res = await axiosAuth().get("/vehicles", { params });
      setVehicles(res.data);
    } catch { toast.error("Error al cargar"); } finally { setLoading(false); }
  }, [axiosAuth, typeFilter]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);
  useEffect(() => {
    axiosAuth().get("/centers").then(r => setCenters(r.data)).catch(() => {});
    axiosAuth().get("/sellers").then(r => setSellers(r.data)).catch(() => {});
  }, [axiosAuth]);

  const f = (key, val) => setForm(p => ({...p, [key]: val}));

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        year: Number(form.year), price: Number(form.price), mileage: Number(form.mileage),
        price_financed: Number(form.price_financed), monthly_payment_from: Number(form.monthly_payment_from),
        finance_entry: Number(form.finance_entry), finance_term_months: Number(form.finance_term_months),
        power_cv: Number(form.power_cv), displacement_cc: Number(form.displacement_cc),
        doors: Number(form.doors), seats: Number(form.seats), warranty_months: Number(form.warranty_months),
        inspection_points: Number(form.inspection_points),
        featured_equipment: form.featured_equipment_text ? form.featured_equipment_text.split("\n").map(s => s.trim()).filter(Boolean) : (form.featured_equipment || []),
        full_equipment: form.full_equipment_text ? form.full_equipment_text.split("\n").map(s => s.trim()).filter(Boolean) : (form.full_equipment || []),
      };
      delete payload.featured_equipment_text;
      delete payload.full_equipment_text;
      if (editingId) {
        await axiosAuth().put(`/vehicles/${editingId}`, payload);
        toast.success("Vehiculo actualizado");
      } else {
        const res = await axiosAuth().post("/vehicles", payload);
        setEditingId(res.data.id);
        toast.success("Vehiculo creado. Puedes subir imagenes ahora.");
        setTab("images");
        return;
      }
      setShowForm(false);
      resetForm();
      fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
  };

  const handleUploadImages = async (files) => {
    if (!files?.length || !editingId) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await axiosAuth().post(`/vehicles/${editingId}/images`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} imagen${files.length > 1 ? "es subidas" : " subida"}`);
      fetchVehicles();
    } catch { toast.error("Error al subir imagenes"); } finally { setUploading(false); }
  };

  const handleDeleteImage = async (imageId) => {
    if (!editingId || !window.confirm("Eliminar esta imagen?")) return;
    try {
      await axiosAuth().delete(`/vehicles/${editingId}/images/${imageId}`);
      toast.success("Imagen eliminada");
      fetchVehicles();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleSetMainImage = async (index) => {
    if (!editingId) return;
    try {
      await axiosAuth().put(`/vehicles/${editingId}/images/main/${index}`);
      toast.success("Portada actualizada");
      fetchVehicles();
    } catch { toast.error("Error al cambiar portada"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este vehiculo?")) return;
    try {
      await axiosAuth().delete(`/vehicles/${id}`);
      toast.success("Vehiculo eliminado");
      fetchVehicles();
    } catch { toast.error("Error al eliminar"); }
  };

  const openEdit = (v) => {
    setEditingId(v.id);
    setForm({
      ...EMPTY_FORM, ...v,
      featured_equipment_text: (v.featured_equipment || []).join("\n"),
      full_equipment_text: (v.full_equipment || []).join("\n"),
    });
    setTab("identity");
    setShowForm(true);
  };

  const resetForm = () => { setEditingId(null); setForm({...EMPTY_FORM}); setTab("identity"); };
  const filtered = vehicles.filter(v => `${v.brand} ${v.model} ${v.version || ""}`.toLowerCase().includes(search.toLowerCase()));

  // Full-page form view
  if (showForm) {
    const currentVehicle = editingId ? vehicles.find(x => x.id === editingId) : null;
    return (
      <div className="space-y-6" data-testid="vehicle-form-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { resetForm(); setShowForm(false); }} className="rounded-sm" data-testid="form-back">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="heading-industrial text-xl font-bold text-slate-900 uppercase">{editingId ? "Editar Vehiculo" : "Nuevo Vehiculo"}</h1>
              {editingId && <p className="text-xs text-slate-400 font-mono">ID: {editingId}</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { resetForm(); setShowForm(false); }} className="rounded-sm text-xs uppercase" data-testid="form-cancel">Cancelar</Button>
            <Button onClick={handleSave} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="form-save">Guardar</Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1 border-b border-slate-200">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-xs uppercase tracking-wider font-semibold whitespace-nowrap rounded-t-sm transition-colors
                ${tab === t.key ? "text-red-600 border-b-2 border-red-600 bg-red-50/50" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          {tab === "identity" && (
            <div className="space-y-4">
              <SectionTitle>Identificacion comercial</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Marca *" value={form.brand} onChange={v => f("brand", v)} testId="f-brand" />
                <Field label="Modelo *" value={form.model} onChange={v => f("model", v)} testId="f-model" />
                <Field label="Version" value={form.version} onChange={v => f("version", v)} testId="f-version" />
                <Field label="Acabado" value={form.trim} onChange={v => f("trim", v)} testId="f-trim" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SelectField label="Tipo de stock" value={form.stock_type} onChange={v => { f("stock_type", v); f("vehicle_type", v); }} testId="f-stock-type"
                  options={[{v:"new",l:"Nuevo"},{v:"km0",l:"KM0"},{v:"used",l:"Ocasion"},{v:"nearly_new",l:"Seminuevo"},{v:"usado",l:"Usado"}]} />
                <SelectField label="Estado" value={form.status} onChange={v => f("status", v)} testId="f-status"
                  options={[{v:"disponible",l:"Disponible"},{v:"reservado",l:"Reservado"},{v:"vendido",l:"Vendido"}]} />
                <Field label="Referencia" value={form.stock_reference} onChange={v => f("stock_reference", v)} testId="f-ref" />
                <SelectField label="Estado comercial" value={form.commercial_status} onChange={v => f("commercial_status", v)} testId="f-commercial"
                  options={[{v:"active",l:"Activo"},{v:"paused",l:"Pausado"},{v:"archived",l:"Archivado"}]} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <CheckField label="Destacado" checked={form.featured} onChange={v => f("featured", v)} />
                <CheckField label="IVA deducible" checked={form.vat_deductible} onChange={v => f("vat_deductible", v)} />
                <Field label="Disponible desde" value={form.available_from} onChange={v => f("available_from", v)} testId="f-available" placeholder="dd/mm/aaaa" />
              </div>
            </div>
          )}

          {tab === "pricing" && (
            <div className="space-y-4">
              <SectionTitle>Precio y financiacion</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Precio contado *" value={form.price} onChange={v => f("price", v)} type="number" testId="f-price" suffix="EUR" />
                <Field label="Precio financiado" value={form.price_financed} onChange={v => f("price_financed", v)} type="number" testId="f-price-fin" suffix="EUR" />
                <Field label="Cuota desde" value={form.monthly_payment_from} onChange={v => f("monthly_payment_from", v)} type="number" testId="f-monthly" suffix="EUR/mes" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Entrada" value={form.finance_entry} onChange={v => f("finance_entry", v)} type="number" testId="f-entry" suffix="EUR" />
                <Field label="Plazo" value={form.finance_term_months} onChange={v => f("finance_term_months", v)} type="number" testId="f-term" suffix="meses" />
              </div>
            </div>
          )}

          {tab === "specs" && (
            <div className="space-y-4">
              <SectionTitle>Especificaciones tecnicas</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Ano *" value={form.year} onChange={v => f("year", v)} type="number" testId="f-year" />
                <Field label="Fecha matriculacion" value={form.registration_date} onChange={v => f("registration_date", v)} testId="f-reg-date" placeholder="mm/aaaa" />
                <Field label="Kilometraje *" value={form.mileage} onChange={v => f("mileage", v)} type="number" testId="f-mileage" suffix="km" />
                <SelectField label="Combustible" value={form.fuel_type} onChange={v => f("fuel_type", v)} testId="f-fuel"
                  options={[{v:"gasolina",l:"Gasolina"},{v:"diesel",l:"Diesel"},{v:"hibrido",l:"Hibrido"},{v:"electrico",l:"Electrico"},{v:"glp",l:"GLP"},{v:"hibrido_enchufable",l:"Hibrido enchufable"}]} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SelectField label="Transmision" value={form.transmission} onChange={v => f("transmission", v)} testId="f-trans"
                  options={[{v:"manual",l:"Manual"},{v:"automatico",l:"Automatico"}]} />
                <Field label="Potencia" value={form.power_cv} onChange={v => f("power_cv", v)} type="number" testId="f-power" suffix="CV" />
                <Field label="Cilindrada" value={form.displacement_cc} onChange={v => f("displacement_cc", v)} type="number" testId="f-cc" suffix="cc" />
                <SelectField label="Etiqueta ambiental" value={form.eco_label} onChange={v => f("eco_label", v)} testId="f-eco"
                  options={[{v:"",l:"Sin asignar"},{v:"0",l:"Cero emisiones"},{v:"eco",l:"ECO"},{v:"c",l:"C"},{v:"b",l:"B"},{v:"none",l:"Sin etiqueta"}]} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SelectField label="Carroceria" value={form.body_type} onChange={v => f("body_type", v)} testId="f-body"
                  options={[{v:"",l:"-"},{v:"Berlina",l:"Berlina"},{v:"SUV",l:"SUV"},{v:"Monovolumen",l:"Monovolumen"},{v:"Familiar",l:"Familiar"},{v:"Coupe",l:"Coupe"},{v:"Cabrio",l:"Cabrio"},{v:"Pick-up",l:"Pick-up"},{v:"Furgoneta",l:"Furgoneta"},{v:"Todoterreno",l:"Todoterreno"}]} />
                <Field label="Color exterior" value={form.color} onChange={v => f("color", v)} testId="f-color" />
                <Field label="Color interior" value={form.color_interior} onChange={v => f("color_interior", v)} testId="f-color-int" />
                <SelectField label="Traccion" value={form.traction} onChange={v => f("traction", v)} testId="f-traction"
                  options={[{v:"",l:"-"},{v:"Delantera",l:"Delantera"},{v:"Trasera",l:"Trasera"},{v:"Total",l:"Total (AWD)"}]} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Puertas" value={form.doors} onChange={v => f("doors", v)} type="number" testId="f-doors" />
                <Field label="Plazas" value={form.seats} onChange={v => f("seats", v)} type="number" testId="f-seats" />
                <Field label="Consumo mixto" value={form.consumption_mixed} onChange={v => f("consumption_mixed", v)} testId="f-consumption" placeholder="5.2 l/100km" />
                <Field label="CO2" value={form.co2_emissions} onChange={v => f("co2_emissions", v)} testId="f-co2" placeholder="120 g/km" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Maletero" value={form.boot_capacity} onChange={v => f("boot_capacity", v)} testId="f-boot" placeholder="350 litros" />
              </div>
            </div>
          )}

          {tab === "trust" && (
            <div className="space-y-4">
              <SectionTitle>Garantia, certificacion y confianza</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Meses de garantia" value={form.warranty_months} onChange={v => f("warranty_months", v)} type="number" testId="f-warranty" />
                <Field label="Puntos de revision" value={form.inspection_points} onChange={v => f("inspection_points", v)} type="number" testId="f-inspect" placeholder="150" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <CheckField label="Garantía ampliable" checked={form.warranty_extendable} onChange={v => f("warranty_extendable", v)} />
                <CheckField label="Km certificado" checked={form.certified_mileage} onChange={v => f("certified_mileage", v)} />
                <CheckField label="Sin danos estructurales" checked={form.structural_damage_free} onChange={v => f("structural_damage_free", v)} />
                <CheckField label="Historial mantenimiento" checked={form.maintenance_history_available} onChange={v => f("maintenance_history_available", v)} />
                <CheckField label="Entrega a domicilio" checked={form.delivery_available} onChange={v => f("delivery_available", v)} />
                <CheckField label="Tasacion / parte de pago" checked={form.trade_in_available} onChange={v => f("trade_in_available", v)} />
              </div>
            </div>
          )}

          {tab === "equipment" && (
            <div className="space-y-4">
              <SectionTitle>Equipamiento</SectionTitle>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Equipamiento destacado (uno por linea)</Label>
                <Textarea value={form.featured_equipment_text} onChange={e => f("featured_equipment_text", e.target.value)}
                  className="rounded-sm border-slate-200 min-h-[100px] font-mono text-xs" data-testid="f-feat-equip"
                  placeholder={"Apple CarPlay y Android Auto\nPantalla tactil 10\"\nCamara trasera\nSensor aparcamiento trasero"} />
                <p className="text-[10px] text-slate-400 mt-1">Se muestran como badges destacados en la ficha publica.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Equipamiento completo (uno por linea)</Label>
                <Textarea value={form.full_equipment_text} onChange={e => f("full_equipment_text", e.target.value)}
                  className="rounded-sm border-slate-200 min-h-[200px] font-mono text-xs" data-testid="f-full-equip"
                  placeholder={"Climatizador bizona\nAsientos calefactados\nVolante multifuncion\nLlantas aleacion 17\"\n..."} />
                <p className="text-[10px] text-slate-400 mt-1">Se agrupan automaticamente por categorias (confort, seguridad, conectividad, etc.).</p>
              </div>
            </div>
          )}

          {tab === "description" && (
            <div className="space-y-4">
              <SectionTitle>Descripcion comercial</SectionTitle>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Descripcion corta</Label>
                <Input value={form.short_description} onChange={e => f("short_description", e.target.value)}
                  className="rounded-sm h-9 border-slate-200" data-testid="f-short-desc" placeholder="Subtitulo comercial breve" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Descripcion larga (comercial)</Label>
                <Textarea value={form.long_description || form.description} onChange={e => { f("long_description", e.target.value); f("description", e.target.value); }}
                  className="rounded-sm border-slate-200 min-h-[250px] text-sm" data-testid="f-long-desc"
                  placeholder={"Unidad en excelente estado. Solo 25.000 km reales. Unico propietario.\n\nEquipamiento completo: climatizador, pantalla tactil, Apple CarPlay...\n\nIdeal para familias. Amplio maletero de 440 litros. Motor eficiente.\n\nRevisado y certificado por nuestros tecnicos. Garantia de 24 meses incluida.\n\nEn Ascauto, concesionario oficial Stellantis, te ofrecemos financiacion a medida, entrega a domicilio y aceptamos tu coche como parte de pago."} />
                <p className="text-[10px] text-slate-400 mt-1">Redacta argumentos de venta, estado de la unidad, ventajas y valor anadido. Se muestra tal cual en la ficha.</p>
              </div>
            </div>
          )}

          {tab === "relations" && (
            <div className="space-y-4">
              <SectionTitle>Centro y vendedor asignado</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Centro</Label>
                  <Select value={form.center_id || "none"} onValueChange={v => f("center_id", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="f-center"><SelectValue placeholder="Seleccionar centro" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Vendedor / Comercial</Label>
                  <Select value={form.seller_id || "none"} onValueChange={v => f("seller_id", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="f-seller"><SelectValue placeholder="Seleccionar vendedor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.center ? `(${s.center})` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[10px] text-slate-400">El vendedor y el centro se muestran en la ficha publica del vehiculo y en los formularios de contacto comercial.</p>
            </div>
          )}

          {tab === "images" && (
            <div className="space-y-4">
              <SectionTitle>Imagenes del vehiculo</SectionTitle>
              {!editingId ? (
                <p className="text-sm text-slate-500">Guarda primero el vehiculo para poder subir imagenes.</p>
              ) : (
                <>
                  {/* Image grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {(currentVehicle?.images || []).map((img, i) => {
                      const isMain = i === (currentVehicle?.main_image_index ?? 0);
                      return (
                        <div key={img.id || i} className={`aspect-[4/3] bg-slate-100 rounded-sm overflow-hidden relative group border-2 ${isMain ? "border-red-500" : "border-slate-200"}`} data-testid={`vehicle-img-${i}`}>
                          <img src={`${API}/files/${img.path}`} alt="" className="w-full h-full object-cover" />
                          {isMain && <Badge className="absolute top-1.5 left-1.5 bg-red-600 text-white text-[9px] rounded-sm font-bold px-2">PORTADA</Badge>}
                          {/* Actions overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-center gap-1 p-2 opacity-0 group-hover:opacity-100">
                            {!isMain && (
                              <button onClick={() => handleSetMainImage(i)} className="bg-white/90 hover:bg-white text-slate-900 rounded-sm px-2 py-1 text-[10px] font-bold uppercase" data-testid={`set-main-${i}`}>
                                Portada
                              </button>
                            )}
                            <button onClick={() => handleDeleteImage(img.id)} className="bg-red-600/90 hover:bg-red-600 text-white rounded-sm px-2 py-1 text-[10px] font-bold uppercase" data-testid={`delete-img-${i}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {/* Upload zone */}
                    <div
                      className="aspect-[4/3] border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center cursor-pointer hover:border-red-400 hover:bg-red-50/30 transition-colors"
                      onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("border-red-500", "bg-red-50/50"); }}
                      onDragLeave={e => { e.currentTarget.classList.remove("border-red-500", "bg-red-50/50"); }}
                      onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("border-red-500", "bg-red-50/50"); handleUploadImages(e.dataTransfer.files); }}
                      data-testid="image-upload-zone"
                    >
                      <input type="file" accept="image/*" multiple onChange={e => handleUploadImages(e.target.files)} className="hidden" id="vehicle-image-upload" data-testid="vehicle-image-input" />
                      <label htmlFor="vehicle-image-upload" className="cursor-pointer text-center p-4">
                        <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <p className="text-xs text-slate-500 font-semibold">{uploading ? "Subiendo..." : "Subir imagenes"}</p>
                        <p className="text-[10px] text-slate-400 mt-1">Seleccion multiple o arrastra aqui</p>
                      </label>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400">Haz clic en "Portada" para cambiar la imagen principal. Arrastra archivos o selecciona varios a la vez. La imagen con borde rojo es la portada publica.</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Bottom save bar */}
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <Button variant="outline" onClick={() => { resetForm(); setShowForm(false); }} className="rounded-sm text-xs uppercase">Cancelar</Button>
          <Button onClick={handleSave} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold px-6" data-testid="form-save-bottom">Guardar vehiculo</Button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="vehicles-title">Vehiculos</h1>
          <p className="text-sm text-slate-500 mt-1">{vehicles.length} vehiculos en inventario</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-5"
            data-testid="new-vehicle-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Vehiculo
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const res = await axiosAuth().get("/export/vehicles", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a"); a.href = url; a.download = "vehiculos.csv"; a.click();
              toast.success("Exportacion descargada");
            } catch { toast.error("Error al exportar"); }
          }} className="rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-3 border-slate-200" data-testid="export-vehicles-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar por marca, modelo o version..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10 rounded-sm h-9 border-slate-200" data-testid="vehicles-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36 rounded-sm h-9 border-slate-200" data-testid="vehicles-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="new">Nuevos</SelectItem>
            <SelectItem value="km0">KM0</SelectItem>
            <SelectItem value="usado">Ocasion</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-64 bg-slate-100 animate-pulse rounded-sm" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">No hay vehiculos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(v => (
            <Card key={v.id} className="border-slate-200 rounded-sm overflow-hidden hover:border-slate-400 transition-colors" data-testid={`admin-vehicle-${v.id}`}>
              <div className="h-40 bg-slate-100 relative">
                {v.images?.length > 0 ? (
                  <img src={`${API}/files/${v.images[0].path}`} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Car className="w-12 h-12 text-slate-300" /></div>
                )}
                <div className="absolute top-2 left-2 flex gap-1">
                  <Badge className={`rounded-sm text-[10px] ${v.stock_type === 'km0' || v.vehicle_type === 'km0' ? 'bg-red-600' : 'bg-slate-900'} text-white`}>
                    {(v.stock_type || v.vehicle_type || "").toUpperCase()}
                  </Badge>
                  <Badge className={`rounded-sm text-[10px] ${v.status === 'disponible' ? 'bg-green-600' : v.status === 'reservado' ? 'bg-amber-600' : 'bg-slate-600'} text-white`}>
                    {v.status}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="heading-industrial text-base font-bold text-slate-900 uppercase">{v.brand} {v.model} {v.version || ""}</h3>
                <p className="text-lg font-bold text-red-600 font-mono">{v.price?.toLocaleString('es-ES')}&euro;</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {v.mileage?.toLocaleString('es-ES')} km</span>
                  <span className="flex items-center gap-1"><Fuel className="w-3 h-3" /> {v.fuel_type}</span>
                  <span className="font-mono">{v.year}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={() => openEdit(v)} className="rounded-sm text-xs flex-1" data-testid={`edit-vehicle-${v.id}`}>Editar</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(v.id)} className="rounded-sm text-xs text-red-600" data-testid={`delete-vehicle-${v.id}`}>Eliminar</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100 mb-3">{children}</h3>;
}
function Field({ label, value, onChange, type = "text", testId, placeholder, suffix }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="relative">
        <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="rounded-sm h-9 border-slate-200"
          data-testid={testId} placeholder={placeholder} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{suffix}</span>}
      </div>
    </div>
  );
}
function SelectField({ label, value, onChange, options, testId }) {
  // Map empty string values to "__none__" to avoid Radix error
  const mappedOptions = options.map(o => ({
    ...o,
    v: o.v === "" ? "__none__" : o.v
  }));
  const mappedValue = value === "" ? "__none__" : (value || "__none__");
  const handleChange = (v) => onChange(v === "__none__" ? "" : v);
  
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <Select value={mappedValue} onValueChange={handleChange}>
        <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {mappedOptions.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
function CheckField({ label, checked, onChange }) {
  return (
    <div className="flex items-center gap-2 bg-slate-50 rounded-sm px-3 py-2">
      <Checkbox checked={!!checked} onCheckedChange={onChange} />
      <label className="text-xs text-slate-700 cursor-pointer select-none">{label}</label>
    </div>
  );
}
